import { BaseSyntheticEvent, useContext, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { ParseStatus } from '@lib/shopify/graphql/client'
import { LanguageContext } from '@lib/language'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { updateShop } from '@lib/print/request'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { getOrCreateShop } from '@lib/print/shop'
import {
  ShopFormStepProps,
  ShopFormShopStepValues,
  ShopFormShopStepShopValues,
} from '@lib/print/types'
import { getPageUrl, PageType } from '@lib/routes'
import { StringsContext } from '@lib/strings-context'
import { useSubmitSignupForm } from '@lib/user/auth'
import { UserContext } from '@lib/user/context'

import Alert from '@components/alert'
import Button from '@components/buttons/button'
import ComplexPortableText from '@components/portable-text/complex'
import SimpleLink from '@components/simple-link'
import ShopFormBackButton from '../back-button'
import ShopStepShopForm from './shop-form'
import ShopStepUserForm from './user-form'

const ShopStep = ({ stepIndex }: ShopFormStepProps) => {
  const strings = useContext(StringsContext)
  const { locale } = useContext(LanguageContext)
  const { printSlots, shopState, dispatchShopState, setActiveStepIndex } =
    useContext(PrintShopFormContext)
  const { authStrings, shopFormStrings } = useContext(
    PrintShopFormStringsContext,
  )
  const { user } = useContext(UserContext)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isShopError, setIsShopError] = useState(false)
  const [defaultShopValues] = useState<ShopFormShopStepShopValues>({
    shopName: shopState.shopName ?? '',
    institutionName: shopState.institutionName ?? '',
  })

  const { handleSubmit, register, setValue, formState } =
    useForm<ShopFormShopStepValues>({
      defaultValues: {
        ...defaultShopValues,
      },
    })

  const [
    signupErrorStatus,
    isSignupError,
    isSignupLoading,
    signupErrorMessages,
    handleSignupSubmit,
  ] = useSubmitSignupForm()

  const isSubmitDisabled = useMemo(() => {
    const hasShopErrors =
      !!formState.errors.shopName || !!formState.errors.institutionName
    const hasUserErrors =
      !!formState.errors.firstName ||
      !!formState.errors.lastName ||
      !!formState.errors.email ||
      !!formState.errors.password

    return hasShopErrors || hasUserErrors
  }, [formState])

  const loginPageUrl = useMemo(() => {
    // Redirect to create shop page, if shop isn't created yet
    let redirectUrl = getPageUrl(PageType.CREATE_SHOP_PAGE)

    // Redirect to edit shop page, if shop is already created
    if (shopState.slug) {
      redirectUrl = getPageUrl(PageType.EDIT_SHOP_PAGE, shopState.slug, {
        parameters: {
          step: 'shop',
        },
      })
    }

    return getPageUrl(PageType.LOGIN_PAGE, null, {
      parameters: {
        redirectUrl,
      },
    })
  }, [printSlots, shopState])

  // Handle user creation and login
  const createUserAndLogin = async (values: ShopFormShopStepValues) => {
    await new Promise<void>(async (resolve, reject) => {
      await handleSignupSubmit(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
        },
        async () => {
          resolve()
        },
        async () => {
          reject()
        },
      )
    })
  }

  // Handle shop update
  const update = async (values: ShopFormShopStepValues) => {
    const newShop = await getOrCreateShop(locale, shopState, dispatchShopState)
    const [, newPrints] = await updateShop(locale, {
      shopId: newShop.id!,
      email: user?.email ?? values.email,
      shopName: values.shopName,
      institutionName: values.institutionName,
    })

    dispatchShopState({
      action: 'updateShopName',
      shopName: values.shopName,
    })
    dispatchShopState({
      action: 'updateInstitutionName',
      institutionName: values.institutionName,
    })

    // Add inherited prints to print list
    newPrints.forEach((shopPrint) => {
      dispatchShopState({
        action: 'addPrint',
        shopPrint,
      })
    })
  }

  // Handle form submission
  const onSubmit = async (
    values: ShopFormShopStepValues,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault()

    setIsSubmitting(true)
    setIsShopError(false)

    try {
      // If user isn't logged in, create a new user
      if (!user?.isLoggedIn) {
        await createUserAndLogin(values)
      }
    } catch (error) {
      console.error(error)

      setIsSubmitting(false)
      return
    }

    try {
      await update(values)

      // Go to next step
      setActiveStepIndex((currentStepIndex) => currentStepIndex + 1)
    } catch (error) {
      console.error(error)

      setIsShopError(true)
    }

    setIsSubmitting(false)
  }

  return (
    <div className="max-w-[740px] mx-auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col sm:min-h-[600px] border border-gray-light bg-white">
          <div className="grow px-5 sm:px-8 pt-8 pb-16 space-y-16">
            <div className="space-y-10">
              <div className="space-y-8">
                <h3 className="leading-tight font-semibold text-[22px] text-center">
                  {shopFormStrings.shopFormShopSectionTitle}
                </h3>

                {!!shopFormStrings.shopFormShopSectionDescription && (
                  <p className="text-center text-base">
                    {shopFormStrings.shopFormShopSectionDescription}
                  </p>
                )}
              </div>

              <div className="max-w-[400px] mx-auto">
                <ShopStepShopForm
                  register={register}
                  setValue={setValue}
                  defaultValues={defaultShopValues}
                  errors={formState.errors}
                />
              </div>
            </div>

            <div className="max-w-[400px] mx-auto space-y-8">
              {!user?.isLoggedIn && (
                <div className="space-y-4">
                  <h3 className="leading-tight font-semibold text-[22px] text-center">
                    {shopFormStrings.shopFormUserSectionTitle}
                  </h3>

                  <div className="flex justify-center">
                    <SimpleLink
                      className="underline text-base text-center"
                      href={loginPageUrl}
                      tabIndex={0}
                    >
                      {shopFormStrings.shopFormLoginLink}
                    </SimpleLink>
                  </div>
                </div>
              )}

              <ShopStepUserForm
                register={register}
                signupErrorMessages={signupErrorMessages}
                errors={formState.errors}
              />

              {isSignupError && (
                <div key="error">
                  <Alert error>
                    <ComplexPortableText
                      content={
                        signupErrorStatus === ParseStatus.INACTIVE_ACCOUNT
                          ? authStrings.signupAccountActivationMessage
                          : authStrings.signupErrorMessage
                      }
                      className="rc-error"
                    />
                  </Alert>
                </div>
              )}

              {isShopError && (
                <div key="error">
                  <Alert error>
                    <ComplexPortableText
                      content={shopFormStrings.shopFormStep1ErrorMessage}
                      className="rc rc-error"
                    />
                  </Alert>
                </div>
              )}
            </div>
          </div>

          <div
            className="grow-0 flex justify-end gap-x-4 py-3 px-6"
            style={{
              boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.03)',
            }}
          >
            {stepIndex > 0 && <ShopFormBackButton />}
            <Button
              type="submit"
              variant="primary"
              color="pink"
              size="normal-small"
              icon="ArrowRight"
              disabled={isSignupLoading || isSubmitting || isSubmitDisabled}
            >
              {isSubmitting
                ? strings.buttonSubmitting
                : shopFormStrings.shopFormNextButton}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default ShopStep
